import { useCallback, useState } from 'react';
import Modal from '@/components/Modals/Modal';
import { useSmallScreen } from '@/hooks/useWindowDimension';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import {
  type FilterState,
  initialState as initialFilterState,
} from '@/features/filterSlice';
import type { RedFlagWarning } from '@/features/wildfireSlice';
import { useFilterLogic } from './useFilterLogic';
import { FilterForm } from './FilterForm';
import { SpanCountWithDisplay } from './SpanCountWithDisplay';

interface LocalFilterProps {
  isOpen: boolean;
  onClose: () => void;
  initialState?: FilterState;
  onApply: (filterState: FilterState) => void;
  redFlagWarnings?: RedFlagWarning[];
  config?: {
    title?: string;
    primaryLabel?: string;
    primaryTestId?: string;
  };
}

export function LocalFilter({
  isOpen,
  onClose,
  initialState = initialFilterState,
  onApply,
  redFlagWarnings,
  config,
}: LocalFilterProps) {
  const intl = useClientTerminology();
  const isSmallScreen = useSmallScreen();

  const handleSubmit = useCallback(
    (filterState: FilterState) => {
      onApply(filterState);
      onClose();
    },
    [onApply, onClose]
  );

  const [filterState, setFilterState] =
    useState<FilterState>(initialFilterState);

  const { formProps, formData, fieldFlags, isFetching } = useFilterLogic({
    initialFilterState: initialState,
    redFlagWarnings,
    onSubmit: handleSubmit,
    onFilterUpdated: setFilterState,
  });

  const title = config?.title || intl.formatMessage({ id: 'filter.title' });
  const primaryLabel =
    config?.primaryLabel || intl.formatMessage({ id: 'filter.showResults' });
  const primaryTestId =
    config?.primaryTestId || 'add-spans-filter-show-results-button';

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={title}
      fullScreen={isSmallScreen}
      primaryButtonTestId={primaryTestId}
      primaryLabel={primaryLabel}
      onPrimaryAction={formProps.handleSubmit}
      secondaryButtonTestId="add-spans-filter-clear-results-button"
      secondaryLabel={intl.formatMessage({ id: 'filter.clear' })}
      onSecondaryAction={formProps.clearForm}
      testId="add-spans-filter-modal"
      footerElements={<SpanCountWithDisplay filterState={filterState} />}
      size={isSmallScreen ? '100%' : '460px'}
    >
      <FilterForm
        register={formProps.register}
        control={formProps.control}
        sortedSpecies={formData.sortedSpecies}
        tags={formData.tags}
        worklog={formData.worklogStatusOptions}
        redFlagWarnings={formData.redFlagWarnings}
        hasWildfireRisk={fieldFlags.hasWildfireRisk}
        hasHazardTrees={fieldFlags.hasHazardTrees}
        hasStrikeTrees={fieldFlags.hasStrikeTrees}
        hasCriticalTrees={fieldFlags.hasCriticalTrees}
        hasOverhang={fieldFlags.hasOverhang}
        hasUpdatedAtYear={fieldFlags.hasUpdatedAtYear}
        isFetching={isFetching}
      />
    </Modal>
  );
}
